import { useAtom, useAtomValue } from 'jotai';
import { Suspense } from 'react';
import { trackGeneralUiInteractionEvent } from '@/analytics/snowplow/analytics';
import { summaryProfileIdAtom } from '@/atoms/radar';
import { SYSTEM_WIDE_USER_ID } from '@/consts/userConfigs';
import { useRadarTemplateOptions } from '@/hooks/useRadarTemplateOptions';
import { PromiseAtom } from '@/types/atom';
import { StringOption } from '@/types/generic';
import { UserConfigRadarTemplate } from '@/types/userConfigs';
import { RadarTemplateDropdownBase } from './RadarTemplateDropdown';

const DROPDOWN_ID = 'summary-profile-dropdown';

const SummaryProfileDropdownWithData = ({
  radarTemplateConfigsAtom,
  label = '',
}: {
  radarTemplateConfigsAtom: PromiseAtom<UserConfigRadarTemplate[]>;
  label?: string;
}) => {
  const radarTemplateConfigs = useAtomValue(radarTemplateConfigsAtom);
  const options = useRadarTemplateOptions(radarTemplateConfigs);

  const [selectedProfileId, setSelectedProfileId] = useAtom(summaryProfileIdAtom);
  const selectedProfile = radarTemplateConfigs.find(template => template.configId === selectedProfileId);

  const allOptions = options.flatMap(group => group.options);
  const value = selectedProfile ? allOptions.find(option => option.value === selectedProfile.configId) : null;

  // Unlike the radar dropdown, the summary profile is a self-contained selection: it only updates its
  // own atom and never touches the shared radarTemplateIdAtom or the RADAR_TEMPLATE URL param, so the
  // radar tab and the main radar page are unaffected.
  const onChange = (option: StringOption) => {
    if (option.value === selectedProfileId) return;

    setSelectedProfileId(option.value);

    const selectedTemplateConfig = radarTemplateConfigs.find(template => template.configId === option.value);
    const isCustomTemplate = !!selectedTemplateConfig && selectedTemplateConfig.hudlUserId !== SYSTEM_WIDE_USER_ID;

    trackGeneralUiInteractionEvent({
      interactionType: 'select',
      itemLocation: 'player summary',
      itemName: `${isCustomTemplate ? 'custom' : 'statsbomb'}: ${option.label}`,
      itemType: 'dropdown',
    });
  };

  return (
    <RadarTemplateDropdownBase id={DROPDOWN_ID} label={label} options={options} value={value} onChange={onChange} />
  );
};

const SummaryProfileDropdown = ({
  radarTemplateConfigsAtom,
  label,
}: {
  radarTemplateConfigsAtom: PromiseAtom<UserConfigRadarTemplate[]>;
  label: string;
}) => (
  <Suspense fallback={<RadarTemplateDropdownBase id={DROPDOWN_ID} label={label} isLoading />}>
    <SummaryProfileDropdownWithData radarTemplateConfigsAtom={radarTemplateConfigsAtom} label={label} />
  </Suspense>
);

export default SummaryProfileDropdown;
